import {
  LookupTrackerStore,
  ValidationError,
} from "@ofqual/portal-core/core/types";
import { combineReducers, AnyAction } from "redux";
import { Attachment } from "../domain/Attachment";
import { Scope } from "../domain/Scope";
import {
  INPUTTING_ITEM,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  LOADED_ITEM,
  LOADING_ITEM,
  SAVED_ITEM,
  UPDATING_ITEM,
  ADD_SCOPE,
  TOGGLE_DELETE_SCOPE,
  SUBMITTING_ITEM,
  SUBMITTING_ITEM_VALIDATION_FAIL,
  SUBMITTED_ITEM,
  RESET_ITEM,
  SUBMITTING_UPDATE_ITEM,
  SUBMITTING_UPDATE_ITEM_VALIDATION_FAIL,
  DELETE_ATTACHMENT,
  UPDATE_SCOPE,
  DELETE_SCOPES,
  LOADED_EVIDENCE,
  LOADED_EVIDENCE_REQUEST,
  LOADING_EVIDENCE_REQUEST,
  UPDATING_EVIDENCE_REQUEST,
  UPDATING_OTHER_EVIDENCE_REQUEST,
  DELETE_EVIDENCE_ATTACHMENT,
  LOADING_ORGANISATION_SCOPES,
  LOADED_ORGANISATION_SCOPES,
  UPDATE_ORGS_PARAMS,
} from "./actions";
import {
  CreateItemState,
  ItemState,
  OrganisationScopesState,
  RequestState,
} from "./state";
import { failedCrudResult, noCrudResult, successCrudResult } from "./types";
import deepmerge from "deepmerge";

const itemInitialState: ItemState = {
  status: "NOT_LOADED",
  item: null,
  cleanScopes: [],
  scopes: [],
  lookupTrackerStore: {},
  attachments: [],
  attachmentsToDelete: [],
  evidenceRequests: [],
  lastCrudResult: noCrudResult(),
};

const item = (
  state: ItemState = itemInitialState,
  action: AnyAction
): ItemState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ITEM:
      return {
        ...state,
        item: null,
        scopes: [],
        attachments: [],
        status: "LOADING",
        lastCrudResult: noCrudResult(),
      };
    case UPDATING_ITEM:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };
    case LOADED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload.item,
        cleanScopes: payload.scopes,
        scopes: payload.scopes,
        attachments: payload.attachments,
        evidenceRequests:
          payload.evidenceRequests === undefined
            ? state.evidenceRequests
            : payload.evidenceRequests,
      };
    case LOADED_EVIDENCE:
      return {
        ...state,
        evidenceRequests: payload,
      };
    case SAVED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload,
        lastCrudResult: successCrudResult(),
      };
    case SUBMITTING_UPDATE_ITEM:
      return {
        ...state,
        status: "SUBMITTING_UPDATE",
      };
    case SUBMITTING_UPDATE_ITEM_VALIDATION_FAIL:
      return {
        ...state,
        lastCrudResult: failedCrudResult(payload as ValidationError),
        status: "LOADED",
      };
    case RESET_ITEM:
      return itemInitialState;
    case ADD_SCOPE:
      return {
        ...state,
        scopes: [...state.scopes, payload],
      };
    case TOGGLE_DELETE_SCOPE:
      const { scopes } = payload as {
        scopes: Scope[];
        shouldDelete: boolean;
      };

      const newScopes = [...state.scopes];
      for (const scope of scopes) {
        const foundIndex = newScopes.findIndex((x) => x.id === scope.id);
        if (foundIndex !== -1) {
          newScopes[foundIndex] = {
            ...newScopes[foundIndex],
            shouldDelete: !scope.shouldDelete,
          };
        }
      }
      return {
        ...state,
        scopes: newScopes,
      };
    case DELETE_SCOPES:
      const items = state.scopes.filter((x) => x.shouldDelete !== true);
      return {
        ...state,
        scopes: items,
      };
    case UPDATE_SCOPE:
      const { updatedScope, lookupTrackingInfo } = payload as {
        updatedScope: Scope;
        lookupTrackingInfo: LookupTrackerStore;
      };
      const updatedScopes = state.scopes.map((item) =>
        item.id === updatedScope.id ? updatedScope : item
      );
      return {
        ...state,
        scopes: updatedScopes,
        lookupTrackerStore: deepmerge(
          state.lookupTrackerStore,
          lookupTrackingInfo
        ),
      };

    case DELETE_ATTACHMENT:
      const { attachment } = payload as {
        attachment: Attachment;
      };
      return {
        ...state,
        attachments: state.attachments.filter((x) => x.id !== attachment.id),
        attachmentsToDelete: [...state.attachmentsToDelete, attachment],
      };
    default:
      return state;
  }
};

const requestInitialState: RequestState = {
  status: "NOT_LOADED",
  request: null,
  lastCrudResult: noCrudResult(),
};

const evidenceRequest = (
  state: RequestState = requestInitialState,
  action: AnyAction
): RequestState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_EVIDENCE_REQUEST:
      return {
        ...state,
        request: null,
        status: "LOADING",
        lastCrudResult: noCrudResult(),
      };
    case UPDATING_EVIDENCE_REQUEST:
    case UPDATING_OTHER_EVIDENCE_REQUEST:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };
    case LOADED_EVIDENCE_REQUEST:
      return {
        ...state,
        request: payload,
        status: "LOADED",
      };
    case DELETE_EVIDENCE_ATTACHMENT:
      const { attachment } = payload as {
        attachment: Attachment;
      };
      return {
        ...state,
        request: state.request
          ? {
              ...state.request,
              attachments: state.request.attachments?.filter(
                (x) => x.id !== attachment.id
              ),
            }
          : null,
      };
    default:
      return state;
  }
};

const createItemInitialState: CreateItemState = {
  status: "INPUTTING",
  lastCrudResult: noCrudResult(),
};
const createItem = (
  state: CreateItemState = createItemInitialState,
  action: AnyAction
): CreateItemState => {
  const { type, payload } = action;
  switch (type) {
    case CREATING_ITEM:
      return {
        ...state,
        status: "CREATING",
      };
    case CREATING_ITEM_VALIDATION_FAIL:
      return {
        ...state,
        lastCrudResult: failedCrudResult(payload as ValidationError),
        status: "INPUTTING",
      };
    case CREATED_ITEM:
      return {
        ...state,
        lastCrudResult: noCrudResult(),
        status: "CREATED",
      };
    case SUBMITTING_ITEM:
      return {
        ...state,
        status: "SUBMITTING",
      };
    case SUBMITTING_ITEM_VALIDATION_FAIL:
      return {
        ...state,
        lastCrudResult: failedCrudResult(payload as ValidationError),
        status: "INPUTTING",
      };
    case SUBMITTED_ITEM:
      return {
        ...state,
        lastCrudResult: noCrudResult(),
        status: "SUBMITTED",
      };
    case INPUTTING_ITEM:
      return {
        ...state,
        lastCrudResult: noCrudResult(),
        status: "INPUTTING",
      };
    default:
      return state;
  }
};

export const organisationScopesInitialState: OrganisationScopesState = {
  status: "NOT_LOADED",
  searchParams: {},
  scopes: null,
};

export const organisationScopes = (
  state: OrganisationScopesState = organisationScopesInitialState,
  action: AnyAction
): OrganisationScopesState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ORGANISATION_SCOPES:
      return {
        ...state,
        status: "LOADING",
        scopes: null,
      };
    case LOADED_ORGANISATION_SCOPES:
      return {
        ...state,
        status: "LOADED",
        scopes: payload,
      };
    case UPDATE_ORGS_PARAMS:
      return {
        ...state,
        searchParams: { ...payload },
      };
  }
  return state;
};

export const rootReducer = combineReducers({
  item,
  createItem,
  evidenceRequest,
  organisationScopes,
});
